import { useMemo } from 'react';
import { PaginatedLog } from '../types';
import { SortOrder } from '../constants';

export interface FlattenedLogItem {
  id: string;
  type: 'log' | 'empty' | 'skeleton';
  log?: PaginatedLog;
  logIndex?: number;
  lineNumber?: number;
  runId: string;
}

/**
 * Hook to flatten logs grouped by runId into a single virtualized array
 *
 * @param logs - Logs grouped by runId: { [runId]: PaginatedLog[] }
 * @param meta - Metadata about the logs (contains total_count, etc)
 * @param total - Total count of logs from server (V2 only, unused for line number calculation)
 * @param isV2 - Whether to use V2 mode
 * @param sortOrder - Sort order: NEWEST or OLDEST
 * @returns Flattened array of log items ready for virtualization
 */
export function useFlattenedLogs(
  logs: { [runId: string]: PaginatedLog[] } | undefined | null,
  meta: any,
  total?: number,
  isV2?: boolean,
  sortOrder?: SortOrder,
): FlattenedLogItem[] {
  return useMemo(() => {
    if (!logs || typeof logs !== 'object') {
      return [];
    }

    const items: FlattenedLogItem[] = [];
    const runIds = Object.keys(logs);

    runIds.forEach((runId) => {
      const runLogs = logs[runId];

      if (!runLogs || runLogs.length === 0) {
        items.push({ id: `empty-${runId}`, type: 'empty', runId });
        return;
      }

      // Process each log
      runLogs.forEach((log, index) => {
        // Calculate incremental line numbers based on position in flattened array
        // Line numbers are sequential (1, 2, 3, 4...) regardless of pagination or sort order
        const globalIndex = items.filter((item) => item.type === 'log').length;
        const lineNumber = globalIndex + 1;

        items.push({
          log,
          runId,
          type: 'log',
          lineNumber,
          logIndex: index,
          id: `${runId}-${log.log_id || lineNumber}`,
        });
      });
    });

    return items;
  }, [logs]);
}
